import React, {useEffect} from 'react';
import {ProductSlider} from './ProductSlider.jsx';
import {TextTeaser} from './TextTeaser.jsx';
import {ErrorTeaser} from './ErrorTeaser.jsx';
import {FocusTeaser} from './FocusTeaser.jsx';
import {DuplexTeaser} from './DuplexTeaser.jsx';
import {TopicCarousel} from './TopicCarousel.jsx';

import {TrackingContextAppend} from '../TrackingContext.jsx';

import {CfPreview} from '../CfPreviewContext.jsx';
import {TextProvider} from '../Text.jsx';
import {OutfitInspiration} from './OutfitInspiration.jsx';
import {LinkBarV2} from './LinkBarV2.jsx';
import {XRay} from '../XRay.jsx';

/**
 * @typedef {Object} TeaserPageTracking
 * @property {string} environment
 * @property {string} entryId
 * @property {PageTrackingData} trackingData
 */

/**
 * @typedef {Object} Device
 * @property {'desktop'|'mobile'|'tablet'|'app'|'unknown'} name
 */

/**
 * @typedef {Object} Separator
 * @property {'SEPARATOR'} type
 */

/**
 * @typedef {Object} ArticleOverviewTeaserContentModel
 * @property {(ProductSliderModel | TextTeaser | FocusTeaserModel | DuplexTeaserModel | TopicCarouselModel | OutfitInspiration | Separator | ErrorTeaserModel | null)[]} content
 * @property {Device} device
 * @property {string|null} contentfulLocale
 * @property {boolean} enablePreview
 * @property {boolean} xrayModeEnabled
 * @property {string} contentType
 * @property {string} space
 * @property {TeaserPageTracking | null} tracking
 * @property {Record<string, string>} internationalTexts
 */

/**
 * @param {ArticleOverviewTeaserContentModel | null} model
 * @returns {JSX.Element}
 */

export function ArticleOverviewTeaserContent({model}) {
    useEffect(() => {
        /** @var {HTMLDivElement} */
        const teaserContentContainer = document.querySelector('.article-overview__teaser-content');
        if (teaserContentContainer) teaserContentContainer.style.height = '';
    }, []);

    if (!model) return null;

    const landmark = model.tracking
        ? {
              cfEnvironment: model.tracking.environment,
              teaserPageEntryId: model.tracking.entryId,
              functionId: model.tracking.trackingData.functionId,
              page: model.tracking.trackingData.trinityClickPage,
          }
        : null;

    return (
        <TrackingContextAppend value={landmark}>
            <CfPreview
                enabled={model.enablePreview}
                space={model.space}
                environment={landmark.cfEnvironment}
                xrayModeEnabled={model.xrayModeEnabled}
            >
                <TextProvider texts={model.internationalTexts}>
                    <div className="content-platform__article-overview-teaser-content">
                        {model.xrayModeEnabled && (
                            <div className="contentful-xray-mode-page-label-container">
                                <XRay
                                    contentType={model.contentType}
                                    fieldId="internalName"
                                    entryId={landmark.teaserPageEntryId}
                                />
                            </div>
                        )}
                        {model.content.map(
                            (teaser) => teaser && <Teaser model={teaser} device={model.device} key={teaser.entryId} />,
                        )}
                    </div>
                </TextProvider>
            </CfPreview>
        </TrackingContextAppend>
    );
}

/**
 * @param {ProductSliderModel | TextTeaser | FocusTeaserModel | DuplexTeaserModel | TopicCarouselModel | OutfitInspiration | Separator | LinkBarV2 | ErrorTeaserModel } model
 * @param {Device} device
 * @returns {JSX.Element}
 */
function Teaser({model, device}) {
    switch (model.type) {
        case 'PRODUCT_SLIDER':
            return <ProductSlider model={model} device={device} />;
        case 'TEXT_TEASER':
            return <TextTeaser model={model} device={device} />;
        case 'FOCUS_TEASER':
            return <FocusTeaser model={model} device={device} />;
        case 'DUPLEX_TEASER':
            return <DuplexTeaser model={model} device={device} />;
        case 'TOPIC_CAROUSEL':
            return <TopicCarousel model={model} device={device} />;
        case 'OUTFIT_INSPIRATION':
            return <OutfitInspiration model={model} device={device} />;
        case 'ERROR_TEASER':
            return <ErrorTeaser model={model} />;
        case 'SEPARATOR':
            return <hr className="content-platform__separator" />;
        case 'LINK_BAR':
            return <LinkBarV2 model={model} />;
        default:
            return null;
    }
}
